import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  setDoc,
  addDoc,
} from "firebase/firestore";

import type { AppDispatch, RootState } from "./store";
import { db } from "../firebase";

/* ─────────────────────────────────────────
   TYPES
   ───────────────────────────────────────── */

export type MerchantMode = "test" | "live";

export type MerchantKeys = {
  pk_live:             string | null;
  pk_test:             string | null;
  secret_key_live:     string | null;
  secret_key_test:     string | null;
  webhook_secret_live: string | null;
  webhook_secret_test: string | null;
  restricted_key_live: string | null;
  restricted_key_test: string | null;
};

export type MerchantCapabilities = {
  card_payments: boolean;
  mobile_money:  boolean;
  payouts:       boolean;
  disputes:      boolean;
};

export type MerchantBalance = {
  available: number;
  pending:   number;
  currency:  string;
};

export type MerchantTransaction = {
  id:                   string;
  object:               string;
  merchant_id:          string;
  amount:               number;
  amount_received:      number;
  currency:             string;
  status:               string;
  payment_method_types: string[];
  description:          string | null;
  customer_email:       string | null;
  customer_name:        string | null;
  customer_phone:       string | null;
  customer_country:     string | null;
  mode:                 MerchantMode;
  cosmoola_fee:         number;
  merchant_net:         number;
  created:              number;
  created_at:           string | null;
  [key: string]: any;
};

export type MerchantCheckoutSession = {
  id:             string;
  object:         string;
  merchant_id:    string;
  payment_intent: string;
  status:         string;
  payment_status: string;
  url:            string;
  amount_total:   number;
  currency:       string;
  success_url:    string;
  cancel_url:     string;
  mode:           MerchantMode;
  created:        number;
  created_at:     string | null;
  [key: string]: any;
};

export type MerchantWebhookEndpoint = {
  id:             string;
  url:            string;
  enabled_events: string[];
  status:         "enabled" | "disabled";
  mode:           MerchantMode;
  created:        number;
};

export type Merchant = {
 
  keys: any;
  accountId:        any;
  keysGenerated:    any;
  merchantId:       string;
  userId:           string;
  email:            string;
  businessName:     string;
  businessType:     string;
  country:          string;
  currency:         string;
  website:          string | null;
  phone:            string | null;
  status:           "active" | "pending" | "suspended";
  liveEnabled:      boolean;
  mode:             MerchantMode;
  capabilities:     MerchantCapabilities;
  balance:          MerchantBalance;
  kycVerified:      boolean;
  webhookEndpoints: MerchantWebhookEndpoint[];
  joinDate:         string | null;
  updatedAt:        string | null;
  metadata?:        Record<string, any>;
};

type MerchantState = {
  merchant:      Merchant | null;
  merchantId:    string | null;
  mode:          MerchantMode;

  merchants:     Merchant[];

  keys:          MerchantKeys;

  transactions:     MerchantTransaction[];
  checkoutSessions: MerchantCheckoutSession[];

  stats: {
    total_volume:    number;
    last_30d_volume: number;
    total_count:     number;
    succeeded:       number;
    failed:          number;
    pending:         number;
    refunded:        number;
    success_rate:    number;
  } | null;

  balance: {
    available: Array<{ amount: number; currency: string }>;
    pending:   Array<{ amount: number; currency: string }>;
    livemode:  boolean;
  } | null;

  unsubscribeTransactions?: () => void;
  unsubscribeSessions?:     () => void;
  unsubscribeMerchant?:     () => void;

  loading: boolean;
  error:   string | null;
};

/* ─────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────── */

const toISO = (ts: any): string | null =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseMerchant = (id: string, raw: Record<string, any>): Merchant => ({
  merchantId: id,
  userId: raw.userId ?? "",
  email: raw.email ?? "",
  businessName: raw.businessName ?? "",
  businessType: raw.businessType ?? "individual",
  country: raw.country ?? "",
  currency: raw.currency ?? "eur",
  website: raw.website ?? null,
  phone: raw.phone ?? null,
  status: raw.status ?? "active",
  liveEnabled: raw.liveEnabled ?? false,
  mode: raw.mode ?? "test",
  capabilities: raw.capabilities ?? { card_payments: true, mobile_money: false, payouts: false, disputes: false },
  balance: raw.balance ?? { available: 0, pending: 0, currency: "eur" },
  kycVerified: raw.kycVerified ?? false,
  webhookEndpoints: raw.webhookEndpoints ?? [],
  joinDate: toISO(raw.joinDate),
  updatedAt: toISO(raw.updatedAt),
  accountId: raw.accountId ?? undefined,
  keysGenerated: raw.keysGenerated ?? undefined,
  keys: undefined
});

const parseTransaction = (id: string, raw: Record<string, any>): MerchantTransaction => ({
  id,
  object:               raw.object              ?? "payment_intent",
  merchant_id:          raw.merchant_id         ?? "",
  amount:               raw.amount              ?? 0,
  amount_received:      raw.amount_received     ?? 0,
  currency:             raw.currency            ?? "eur",
  status:               raw.status              ?? "pending",
  payment_method_types: raw.payment_method_types ?? ["card"],
  description:          raw.description         ?? null,
  customer_email:       raw.customer_email      ?? null,
  customer_name:        raw.customer_name       ?? null,
  customer_phone:       raw.customer_phone      ?? null,
  customer_country:     raw.customer_country    ?? null,
  mode:                 raw.mode                ?? "test",
  cosmoola_fee:         raw.cosmoola_fee        ?? 0,
  merchant_net:         raw.merchant_net        ?? 0,
  created:              raw.created             ?? 0,
  created_at:           toISO(raw.created_at),
  ...raw,
});

const parseSession = (id: string, raw: Record<string, any>): MerchantCheckoutSession => ({
  id,
  object:         raw.object          ?? "checkout.session",
  merchant_id:    raw.merchant_id     ?? "",
  payment_intent: raw.payment_intent  ?? "",
  status:         raw.status          ?? "open",
  payment_status: raw.payment_status  ?? "unpaid",
  url:            raw.url             ?? "",
  amount_total:   raw.amount_total    ?? 0,
  currency:       raw.currency        ?? "eur",
  success_url:    raw.success_url     ?? "",
  cancel_url:     raw.cancel_url      ?? "",
  mode:           raw.mode            ?? "test",
  created:        raw.created         ?? 0,
  created_at:     toISO(raw.created_at),
  ...raw,
});

/* ─────────────────────────────────────────
   PERSIST KEYS TO LOCALSTORAGE
   ───────────────────────────────────────── */

const KEYS_STORAGE_KEY = "cosmoola_merchant_keys";
const MODE_STORAGE_KEY = "cosmoola_merchant_mode";

const emptyKeys = (): MerchantKeys => ({
  pk_live:             null,
  pk_test:             null,
  secret_key_live:     null,
  secret_key_test:     null,
  webhook_secret_live: null,
  webhook_secret_test: null,
  restricted_key_live: null,
  restricted_key_test: null,
});

const loadPersistedKeys = (): MerchantKeys => {
  try {
    const raw = localStorage.getItem(KEYS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : emptyKeys();
  } catch { return emptyKeys(); }
};

const persistKeys = (keys: MerchantKeys) => {
  try { localStorage.setItem(KEYS_STORAGE_KEY, JSON.stringify(keys)); } catch {}
};

const clearPersistedKeys = () => {
  try { localStorage.removeItem(KEYS_STORAGE_KEY); } catch {}
};

const loadPersistedMode = (): MerchantMode => {
  try { return (localStorage.getItem(MODE_STORAGE_KEY) as MerchantMode) || "test"; } catch { return "test"; }
};

const persistMode = (mode: MerchantMode) => {
  try { localStorage.setItem(MODE_STORAGE_KEY, mode); } catch {}
};

/* ─────────────────────────────────────────
   INITIAL STATE
   ───────────────────────────────────────── */

const initialState: MerchantState = {
  merchant:         null,
  merchantId:       null,
  mode:             loadPersistedMode(),
  merchants:        [],
  keys:             loadPersistedKeys(),
  transactions:     [],
  checkoutSessions: [],
  stats:            null,
  balance:          null,
  unsubscribeTransactions: undefined,
  unsubscribeSessions:     undefined,
  unsubscribeMerchant:     undefined,
  loading:          false,
  error:            null,
};

/* ─────────────────────────────────────────
   ASYNC THUNKS
   ───────────────────────────────────────── */

/**
 * Upsert a merchant.
 * - If merchantId provided → update that doc (setDoc merge).
 * - If no merchantId → create new doc with auto-generated Firestore ID.
 *   userId is stored inside every doc so we can query all merchants for a user.
 */
export const upsertMerchant = createAsyncThunk(
  "merchant/upsertMerchant",
  async (userData: Partial<Merchant> & { userId: string }) => {
    if (!userData.userId) throw new Error("userId is required");

    let merchantRef;

    if (userData.merchantId) {
      // UPDATE existing merchant doc
      merchantRef = doc(db, "merchants", userData.merchantId);
      const { merchantId: _id, ...rest } = userData;
      await setDoc(merchantRef, { ...rest, updatedAt: serverTimestamp() }, { merge: true });
    } else {
      // CREATE new merchant
      const docData = {
        ...userData,
        status:       "active",
        liveEnabled:  false,
        mode:         "test",
        kycVerified:  false,
        keysGenerated: false,
        capabilities: { card_payments: true, mobile_money: false, payouts: false, disputes: false },
        balance:      { available: 0, pending: 0, currency: userData.currency ?? "eur" },
        webhookEndpoints: [],
        joinDate:     serverTimestamp(),
        updatedAt:    serverTimestamp(),
      };
      const newDoc = await addDoc(collection(db, "merchants"), docData);
      merchantRef = newDoc;
      // Write merchantId into the doc itself for convenience
      await setDoc(doc(db, "merchants", newDoc.id), { merchantId: newDoc.id }, { merge: true });
    }

    const finalSnap = await getDoc(
      merchantRef instanceof Object && "id" in merchantRef
        ? doc(db, "merchants", merchantRef.id)
        : merchantRef
    );
    return finalSnap.exists() ? parseMerchant(finalSnap.id, finalSnap.data()!) : null;
  }
);

/**
 * Fetch ALL merchants belonging to a userId.
 * Each merchant doc has a `userId` field matching the Firebase Auth uid.
 */
export const fetchUserMerchants = createAsyncThunk<Merchant[], string>(
  "merchant/fetchUserMerchants",
  async (userId, { rejectWithValue }) => {
    if (!userId) return rejectWithValue("User ID is required");

    try {
      const q = query(
        collection(db, "merchants"),
        where("userId", "==", userId)
      );

      const snap = await getDocs(q);

      const merchants = snap.docs
        .map((d) => parseMerchant(d.id, d.data()))
        .sort((a, b) => {
          if (!a.joinDate || !b.joinDate) return 0;
          return new Date(b.joinDate).getTime() - new Date(a.joinDate).getTime();
        });

      return merchants;
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch merchants");
    }
  }
);

export const fetchMerchantBusinesses = createAsyncThunk<Merchant[], string>(
  "merchant/fetchMerchantBusinesses",
  async (userId, { rejectWithValue }) => {
    if (!userId) return rejectWithValue("No user ID provided");

    try {
      const q = query(
        collection(db, "merchants"),
        where("userId", "==", userId)
      );

      const snap = await getDocs(q);

      const merchants = snap.docs
        .map((d) => parseMerchant(d.id, d.data()))
        .sort((a, b) => {
          if (!a.joinDate || !b.joinDate) return 0;
          return new Date(b.joinDate).getTime() - new Date(a.joinDate).getTime();
        });

      return merchants;
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch merchants");
    }
  }
);


export const fetchMerchantByUserId = createAsyncThunk<Merchant[], string>(
  "merchant/fetchMerchantByUserId",
  async (userId, { rejectWithValue }) => {
    if (!userId) return rejectWithValue("User ID is required");

    try {
      const q = query(
        collection(db, "merchants"),
        where("userId", "==", userId)
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        return rejectWithValue("No merchants found for this user");
      }

      const merchants = snap.docs
        .map((d) => parseMerchant(d.id, d.data()))
        .sort((a, b) => {
          if (!a.joinDate || !b.joinDate) return 0;
          return new Date(b.joinDate).getTime() - new Date(a.joinDate).getTime();
        });

      return merchants;
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch merchants by userId");
    }
  }
);


// Fetch single merchant profile by merchantId
export const fetchMerchant = createAsyncThunk<Merchant, string>(
  "merchant/fetchMerchant",
  async (merchantId, { rejectWithValue }) => {
    try {
      const snap = await getDoc(doc(db, "merchants", merchantId));
      if (!snap.exists()) return rejectWithValue("Merchant not found");
      return parseMerchant(snap.id, snap.data());
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch merchant");
    }
  }
);


// Fetch transactions (one-time)
export const fetchMerchantTransactions = createAsyncThunk<
  MerchantTransaction[],
  { merchantId: string; mode: MerchantMode; maxResults?: number }
>(
  "merchant/fetchTransactions",
  async ({ merchantId, mode, maxResults = 100 }, { rejectWithValue }) => {
    try {
      const q = query(
        collection(db, "merchantTransactions", merchantId, "transactions"),
        where("mode", "==", mode)
      );

      const snap = await getDocs(q);

      const txs = snap.docs
        .map((d) => parseTransaction(d.id, d.data()))
        .sort((a, b) => b.created - a.created)
        .slice(0, maxResults);

      return txs;
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch transactions");
    }
  }
);

// Fetch dashboard stats + balance
export const fetchDashboardData = createAsyncThunk<
  { stats: any; balance: any },
  { merchantId: string; secretKey: string; functionsUrl: string }
>(
  "merchant/fetchDashboardData",
  async ({ secretKey, functionsUrl }, { rejectWithValue }) => {
    try {
      const [dashRes, balRes] = await Promise.all([
        fetch(`${functionsUrl}/getDashboard`, { headers: { Authorization: `Bearer ${secretKey}` } }),
        fetch(`${functionsUrl}/getBalance`,   { headers: { Authorization: `Bearer ${secretKey}` } }),
      ]);
      const [dash, bal] = await Promise.all([dashRes.json(), balRes.json()]);
      return { stats: dash.stats, balance: bal };
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch dashboard data");
    }
  }
);


export const fetchCheckoutSessions = createAsyncThunk<
  MerchantCheckoutSession[],
  { merchantId: string; mode: MerchantMode }
>(
  "merchant/fetchCheckoutSessions",
  async ({ merchantId, mode }, { rejectWithValue }) => {
    try {
      const q = query(
        collection(db, "checkoutSessions"),
        where("merchant_id", "==", merchantId),
        where("mode", "==", mode)
      );

      const snap = await getDocs(q);

      const sessions = snap.docs
        .map((d) => parseSession(d.id, d.data()))
        .sort((a, b) => b.created - a.created);

      return sessions;
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch sessions");
    }
  }
);

// Delete a payment intent
export const deleteMerchantTransaction = createAsyncThunk<
  string,
  { merchantId: string; transactionId: string }
>(
  "merchant/deleteTransaction",
  async ({ merchantId, transactionId }, { rejectWithValue }) => {
    try {
      await deleteDoc(
        doc(db, "merchantTransactions", merchantId, "transactions", transactionId)
      );
      return transactionId;
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to delete transaction");
    }
  }
);

// Delete a checkout session
export const deleteMerchantCheckoutSession = createAsyncThunk<string, string>(
  "merchant/deleteCheckoutSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, "checkoutSessions", sessionId));
      return sessionId;
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to delete checkout session");
    }
  }
);

// Rotate API keys
export const rotateMerchantKeys = createAsyncThunk<
  Partial<MerchantKeys>,
  { secretKey: string; keyType?: string; functionsUrl: string }
>(
  "merchant/rotateKeys",
  async ({ secretKey, keyType = "all", functionsUrl }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${functionsUrl}/rotateMerchantKeys`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${secretKey}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ key_type: keyType }),
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data?.error?.message || "Rotation failed");

      const k = data.keys || {};
      return {
        pk_live:             k.publishable_key_live  ?? null,
        pk_test:             k.publishable_key_test  ?? null,
        secret_key_live:     k.secret_key_live       ?? null,
        secret_key_test:     k.secret_key_test       ?? null,
        webhook_secret_live: k.webhook_secret_live   ?? null,
        webhook_secret_test: k.webhook_secret_test   ?? null,
        restricted_key_live: k.restricted_key_live   ?? null,
        restricted_key_test: k.restricted_key_test   ?? null,
      };
    } catch (e: any) {
      return rejectWithValue(e.message || "Rotation request failed");
    }
  }
);

/* ─────────────────────────────────────────
   REAL-TIME LISTENERS
   ───────────────────────────────────────── */

export const listenToMerchant =
  (merchantId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!merchantId) return;

    const { unsubscribeMerchant } = getState().merchant;
    if (unsubscribeMerchant) {
      unsubscribeMerchant();
      dispatch(setUnsubscribeMerchant(undefined));
    }

    const ref = doc(db, "merchants", merchantId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const data   = snap.data();
        const parsed = parseMerchant(snap.id, data);

        dispatch(setMerchant(parsed));
        dispatch(setMerchantId(merchantId));

        const existingKeys = getState().merchant.keys;
        const updatedKeys: MerchantKeys = {
          ...existingKeys,
          pk_live: data.keys?.pk_live ?? existingKeys.pk_live,
          pk_test: data.keys?.pk_test ?? existingKeys.pk_test,
        };
        dispatch(setKeys(updatedKeys));
        persistKeys(updatedKeys);
      },
      (error) => {
        console.error("❌ merchant listener error:", error.message);
        dispatch(setError(error.message));
      }
    );

    dispatch(setUnsubscribeMerchant(unsub));
  };

export const listenToMerchantTransactions =
  (merchantId: string, mode: MerchantMode) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!merchantId) return;

    const { unsubscribeTransactions } = getState().merchant;
    if (unsubscribeTransactions) {
      unsubscribeTransactions();
      dispatch(setUnsubscribeTransactions(undefined));
    }

    const q = query(
      collection(db, "merchantTransactions", merchantId, "transactions"),
      where("mode", "==", mode),
      orderBy("created", "desc"),
      limit(100)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const txs = snap.docs.map((d) => parseTransaction(d.id, d.data()));
        dispatch(setTransactions(txs));
      },
      (error) => {
        console.error("❌ merchant transactions listener error:", error.message);
        dispatch(setError(error.message));
      }
    );

    dispatch(setUnsubscribeTransactions(unsub));
  };

export const listenToCheckoutSessions =
  (merchantId: string, mode: MerchantMode) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!merchantId) return;

    const { unsubscribeSessions } = getState().merchant;
    if (unsubscribeSessions) {
      unsubscribeSessions();
      dispatch(setUnsubscribeSessions(undefined));
    }

    const q = query(
      collection(db, "checkoutSessions"),
      where("merchant_id", "==", merchantId),
      where("mode", "==", mode),
      orderBy("created", "desc"),
      limit(50)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const sessions = snap.docs.map((d) => parseSession(d.id, d.data()));
        dispatch(setCheckoutSessions(sessions));
      },
      (error) => {
        console.error("❌ checkout sessions listener error:", error.message);
        dispatch(setError(error.message));
      }
    );

    dispatch(setUnsubscribeSessions(unsub));
  };

export const listenToAllMerchantData =
  (merchantId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!merchantId) return;
    const mode = getState().merchant.mode;
    dispatch(listenToMerchant(merchantId));
    dispatch(listenToMerchantTransactions(merchantId, mode));
    dispatch(listenToCheckoutSessions(merchantId, mode));
  };

export const cleanupMerchantListeners =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { unsubscribeTransactions, unsubscribeSessions, unsubscribeMerchant } = getState().merchant;
    if (unsubscribeTransactions) unsubscribeTransactions();
    if (unsubscribeSessions)     unsubscribeSessions();
    if (unsubscribeMerchant)     unsubscribeMerchant();
    dispatch(setUnsubscribeTransactions(undefined));
    dispatch(setUnsubscribeSessions(undefined));
    dispatch(setUnsubscribeMerchant(undefined));
  };

/* ─────────────────────────────────────────
   SLICE
   ───────────────────────────────────────── */

const merchantSlice = createSlice({
  name: "merchant",
  initialState,
  reducers: {
    setMerchant: (state, action: PayloadAction<Merchant>) => {
      state.merchant   = action.payload;
      state.merchantId = action.payload.merchantId;
    },
    setMerchantId: (state, action: PayloadAction<string>) => {
      state.merchantId = action.payload;
    },
    setMerchants: (state, action: PayloadAction<Merchant[]>) => {
      state.merchants = action.payload;
    },

    setKeys: (state, action: PayloadAction<Partial<MerchantKeys>>) => {
      state.keys = { ...state.keys, ...action.payload };
      persistKeys(state.keys);
    },

    storeRegistrationKeys: (state, action: PayloadAction<{
      merchantId: string;
      keys: {
        publishable_key_live?:  string;
        publishable_key_test?:  string;
        secret_key_live?:       string;
        secret_key_test?:       string;
        webhook_secret_live?:   string;
        webhook_secret_test?:   string;
        restricted_key_live?:   string;
        restricted_key_test?:   string;
      };
    }>) => {
      const k = action.payload.keys;
      state.merchantId = action.payload.merchantId;
      state.keys = {
        pk_live:             k.publishable_key_live  ?? null,
        pk_test:             k.publishable_key_test  ?? null,
        secret_key_live:     k.secret_key_live       ?? null,
        secret_key_test:     k.secret_key_test       ?? null,
        webhook_secret_live: k.webhook_secret_live   ?? null,
        webhook_secret_test: k.webhook_secret_test   ?? null,
        restricted_key_live: k.restricted_key_live   ?? null,
        restricted_key_test: k.restricted_key_test   ?? null,
      };
      persistKeys(state.keys);
    },

    setMode: (state, action: PayloadAction<MerchantMode>) => {
      state.mode = action.payload;
      persistMode(action.payload);
    },

    setTransactions:     (state, action: PayloadAction<MerchantTransaction[]>)     => { state.transactions     = action.payload; },
    setCheckoutSessions: (state, action: PayloadAction<MerchantCheckoutSession[]>) => { state.checkoutSessions = action.payload; },
    setStats:            (state, action: PayloadAction<any>)                        => { state.stats            = action.payload; },
    setBalance:          (state, action: PayloadAction<any>)                        => { state.balance          = action.payload; },

    setUnsubscribeTransactions: (state, action: PayloadAction<(() => void) | undefined>) => { state.unsubscribeTransactions = action.payload; },
    setUnsubscribeSessions:     (state, action: PayloadAction<(() => void) | undefined>) => { state.unsubscribeSessions     = action.payload; },
    setUnsubscribeMerchant:     (state, action: PayloadAction<(() => void) | undefined>) => { state.unsubscribeMerchant     = action.payload; },

    setError: (state, action: PayloadAction<string | null>) => { state.error = action.payload; },

    resetMerchantState: (state) => {
      state.merchant         = null;
      state.merchantId       = null;
      state.merchants        = [];
      state.keys             = emptyKeys();
      state.transactions     = [];
      state.checkoutSessions = [];
      state.stats            = null;
      state.balance          = null;
      state.error            = null;
      clearPersistedKeys();
    },
  },

  extraReducers: (builder) => {
    /* upsertMerchant */
    builder
      .addCase(upsertMerchant.pending,  (state) => { state.loading = true; state.error = null; })
      .addCase(upsertMerchant.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.merchant   = action.payload;
          state.merchantId = action.payload.merchantId;
          const idx = state.merchants.findIndex((m) => m.merchantId === action.payload!.merchantId);
          if (idx >= 0) {
            state.merchants[idx] = action.payload;
          } else {
            state.merchants.unshift(action.payload);
          }
        }
      })
      .addCase(upsertMerchant.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.error.message ?? "Failed to upsert merchant";
      });

    /* fetchUserMerchants */
    builder
      .addCase(fetchUserMerchants.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUserMerchants.fulfilled, (state, action) => {
        state.loading   = false;
        state.merchants = action.payload;
      })
      .addCase(fetchUserMerchants.rejected,  (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      });

    /* fetchMerchantBusinesses */
    builder
      .addCase(fetchMerchantBusinesses.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMerchantBusinesses.fulfilled, (state, action) => {
        state.loading   = false;
        state.merchants = action.payload;
      })
      .addCase(fetchMerchantBusinesses.rejected,  (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      });

    /* fetchMerchant */
    builder
      .addCase(fetchMerchant.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMerchant.fulfilled, (state, action) => {
        state.loading    = false;
        state.merchant   = action.payload;
        state.merchantId = action.payload.merchantId;
        const raw = action.payload as any;
        if (raw._rawKeys) {
          state.keys = { ...state.keys, pk_live: raw._rawKeys.pk_live, pk_test: raw._rawKeys.pk_test };
          persistKeys(state.keys);
        }
      })
      .addCase(fetchMerchant.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      });

    /* fetchMerchantByUserId */
    builder
      .addCase(fetchMerchantByUserId.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMerchantByUserId.fulfilled, (state, action) => {
        state.loading   = false;
        state.merchants = action.payload;
        // Set the first one as active merchant if none selected
        if (action.payload.length > 0 && !state.merchantId) {
          state.merchant   = action.payload[0];
          state.merchantId = action.payload[0].merchantId;
        }
      })
      .addCase(fetchMerchantByUserId.rejected,  (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      });

    /* fetchMerchantTransactions */
    builder
      .addCase(fetchMerchantTransactions.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMerchantTransactions.fulfilled, (state, action) => { state.loading = false; state.transactions = action.payload; })
      .addCase(fetchMerchantTransactions.rejected,  (state, action) => { state.loading = false; state.error = action.payload as string; });

    /* fetchDashboardData */
    builder
      .addCase(fetchDashboardData.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.stats   = action.payload.stats;
        state.balance = action.payload.balance;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      });

    /* deleteMerchantTransaction */
    builder
      .addCase(deleteMerchantTransaction.fulfilled, (state, action) => {
        state.transactions = state.transactions.filter((t) => t.id !== action.payload);
      })
      .addCase(deleteMerchantTransaction.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    /* deleteMerchantCheckoutSession */
    builder
      .addCase(deleteMerchantCheckoutSession.fulfilled, (state, action) => {
        state.checkoutSessions = state.checkoutSessions.filter((s) => s.id !== action.payload);
      })
      .addCase(deleteMerchantCheckoutSession.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    /* rotateMerchantKeys */
    builder
      .addCase(rotateMerchantKeys.fulfilled, (state, action) => {
        state.keys = { ...state.keys, ...action.payload };
        persistKeys(state.keys);
      })
      .addCase(rotateMerchantKeys.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setMerchant,
  setMerchantId,
  setMerchants,
  setKeys,
  storeRegistrationKeys,
  setMode,
  setTransactions,
  setCheckoutSessions,
  setStats,
  setBalance,
  setUnsubscribeTransactions,
  setUnsubscribeSessions,
  setUnsubscribeMerchant,
  setError,
  resetMerchantState,
} = merchantSlice.actions;

export default merchantSlice.reducer;

/* ─────────────────────────────────────────
   SELECTORS
   ───────────────────────────────────────── */

export const selectMerchant         = (s: RootState) => s.merchant.merchant;
export const selectMerchantId       = (s: RootState) => s.merchant.merchantId;
export const selectMerchants        = (s: RootState) => s.merchant.merchants;
export const selectMode             = (s: RootState) => s.merchant.mode;
export const selectIsLive           = (s: RootState) => s.merchant.mode === "live";
export const selectKeys             = (s: RootState) => s.merchant.keys;
export const selectMerchantLoading  = (s: RootState) => s.merchant.loading;
export const selectMerchantError    = (s: RootState) => s.merchant.error;
export const selectTransactions     = (s: RootState) => s.merchant.transactions;
export const selectCheckoutSessions = (s: RootState) => s.merchant.checkoutSessions;
export const selectStats            = (s: RootState) => s.merchant.stats;
export const selectBalance          = (s: RootState) => s.merchant.balance;
export const selectLiveEnabled      = (s: RootState) => s.merchant.merchant?.liveEnabled ?? false;

export const selectSecretKey = (s: RootState): string | null =>
  s.merchant.mode === "live"
    ? s.merchant.keys.secret_key_live
    : s.merchant.keys.secret_key_test;

export const selectPublishableKey = (s: RootState): string | null =>
  s.merchant.mode === "live"
    ? s.merchant.keys.pk_live
    : s.merchant.keys.pk_test;

export const selectWebhookSecret = (s: RootState): string | null =>
  s.merchant.mode === "live"
    ? s.merchant.keys.webhook_secret_live
    : s.merchant.keys.webhook_secret_test;